import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { StaticDatePicker } from '@mui/x-date-pickers/StaticDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import {
  Box,
  TextField,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress
} from '@mui/material';
import axios from 'axios';
import { ITruckOdometer } from 'app/shared/model/truck-odometer.model';
import { convertDateTimeToPtBRWithoutHour } from 'app/shared/util/date-utils';
import dayjs, { Dayjs } from 'dayjs';
import React, { useCallback, useEffect, useState } from 'react';
import { Translate } from 'react-jhipster';
import EditCalendarIcon from '@mui/icons-material/EditCalendar'
import { DateInput } from 'app/shared/components/date-input/DateInput';
import { useTheme } from '@mui/material/styles';

interface TireVehicleOdometerFieldProps {
  truckImplementId: number | string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}

const apiUrl = 'api/truck-odometers';

const TireVehicleOdometerField = ({
  truckImplementId,
  value,
  onChange,
  disabled = false
}: TireVehicleOdometerFieldProps) => {
  const [loading, setLoading] = useState(false);
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [pickerDate, setPickerDate] = useState<Dayjs | null>(null);
  const [selectedReferenceDate, setSelectedReferenceDate] = useState<string | null>(null);
  const theme = useTheme();

  const fetchOdometer = useCallback(
    (referenceDate?: string | null) => {
      if (!truckImplementId) return;

      setLoading(true);
      const params = referenceDate ? { referenceDate } : {};

      axios
        .get<ITruckOdometer>(`${apiUrl}/truck-implement/${truckImplementId}`, { params })
        .then(response => {
          const totalOdometer = response.data?.totalOdometer;
          if (totalOdometer !== null && totalOdometer !== undefined && totalOdometer !== '') {
            onChange(String(totalOdometer));
          } else if (totalOdometer === null || totalOdometer === undefined) {
            onChange('');
          }
          setUpdatedAt(response.data?.updatedAt ?? null);
        })
        .catch(() => {
          onChange('');
          setUpdatedAt(null);
        })
        .then(() => setLoading(false));
    },
    [truckImplementId, onChange]
  );

  useEffect(() => {
    fetchOdometer();
  }, [fetchOdometer]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value;
    if (/^\d*([,.]\d*)?$/.test(inputValue)) {
      onChange(inputValue.replace(',', '.'));
    }
  };

  const openCalendarModal = () => {
    setPickerDate(selectedReferenceDate ? dayjs(selectedReferenceDate) : dayjs());
    setCalendarOpen(true);
  };

  const applySelectedDate = () => {
    if (!pickerDate) return;

    const referenceDate = pickerDate.format('YYYY-MM-DD');
    setSelectedReferenceDate(referenceDate);
    setCalendarOpen(false);
    fetchOdometer(referenceDate);
  };

  const resetToLatest = () => {
    setSelectedReferenceDate(null);
    setPickerDate(null);
    setCalendarOpen(false);
    fetchOdometer();
  };

  const getReferenceDateLabel = () => {
    if (updatedAt) {
      return convertDateTimeToPtBRWithoutHour(updatedAt);
    }
    return null;
  };

  const referenceDateLabel = getReferenceDateLabel();

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <Typography variant="subtitle1" color="text.primary">
          <Translate contentKey="fleetSenseApp.tireTruckImplement.installVehicleOdometer" />
        </Typography>

        {referenceDateLabel && (
          <Typography variant="subtitle1" color="text.primary">
            <Translate contentKey="fleetSenseApp.tireTruckImplement.odometerReferenceDate" />:{' '}
            <b>{referenceDateLabel}</b>
          </Typography>
        )}
      </Box>

      {loading ? (
        <CircularProgress size={20} />
      ) : (
        <>
          <Box position="relative">
            <TextField
              fullWidth
              size="small"
              id="tire-vehicle-odometer"
              value={value}
              onChange={handleChange}
              disabled={disabled}
              inputProps={{
                inputMode: 'decimal',
                pattern: '\\d*([,.]\\d*)?'
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'transparent',
                },
                '& .MuiOutlinedInput-input': {
                  color: theme.palette.text.primary,
                },
                '& .MuiOutlinedInput-input:-webkit-autofill': {
                  WebkitBoxShadow: '0 0 0 1000px transparent inset',
                  WebkitTextFillColor: theme.palette.text.primary,
                  transition: 'background-color 9999s ease-in-out 0s',
                  caretColor: theme.palette.text.primary,
                },
                '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#808080',
                },
                '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.text.primary,
                },
                '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.secondary.main,
                },
              }}
            />

            <IconButton
              onClick={openCalendarModal}
              disabled={disabled}
              sx={{
                position: 'absolute',
                right: 8,
                top: '50%',
                transform: 'translateY(-50%)'
              }}
            >
              <EditCalendarIcon sx={{ color: 'text.primary' }} />
            </IconButton>
          </Box>

          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 3 }}>
            <Translate contentKey="fleetSenseApp.tireTruckImplement.odometerManualHint" />
          </Typography>
        </>
      )}

      <Dialog open={calendarOpen} onClose={() => setCalendarOpen(false)}>
        <DialogTitle>
          <Translate contentKey="fleetSenseApp.tireTruckImplement.odometerSelectDate" />
        </DialogTitle>

        <DialogContent
          sx={{
            display: 'flex',
            justifyContent: 'center',
            overflow: 'hidden',

            '& .MuiPickersDay-root.Mui-selected': {
              backgroundColor: `${theme.palette.primary.main}`,
              color: `${theme.palette.primary.contrastText}`,
              fontWeight: 700,
              border: `2px solid ${theme.palette.success.main}`,
            },

            '& .MuiPickersDay-root.Mui-selected:hover': {
              backgroundColor: `${theme.palette.primary.dark}`,
            },
          }}
        >
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <StaticDatePicker
              displayStaticWrapperAs="desktop"
              value={pickerDate}
              disableFuture
              onChange={(newValue: Dayjs | null) => {
                setPickerDate(newValue);
              }}
              renderInput={(params) => <TextField {...params} />}
            />
          </LocalizationProvider>
        </DialogContent>

        <DialogActions>
          {selectedReferenceDate && (
            <Button variant="contained" onClick={resetToLatest}>
              <Translate contentKey="fleetSenseApp.tireTruckImplement.odometerUseLatest" />
            </Button>
          )}

          <Button variant="contained" onClick={applySelectedDate} disabled={!pickerDate}>
            <Translate contentKey="entity.action.confirm" />
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TireVehicleOdometerField;
